"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
// import { check } from '../common/check';
const common_1 = require("../common");
const CreaseEdgeRef_1 = require("../core/CreaseEdgeRef");
const CreaseVertex_1 = require("../core/CreaseVertex");
/**
 * A helper class used to represent one or more merged vertices for analysis.
 * @private
 */
class MetaVertex extends CreaseVertex_1.CreaseVertex {
    constructor(index, vertex) {
        super(index, vertex.position);
        this._vertices = [vertex];
        this._deactivated = false;
        this._isDestroyed = false;
    }
    /**
     * Returns the index of this vertex.
     * @returns {number} The index of this vertex.
     */
    get index() {
        return this._index;
    }
    /**
     * Sets the index of this vertex.
     * @param {number} index An index.
     */
    set index(index) {
        this._index = index;
    }
    get deactivated() {
        return this._deactivated;
    }
    set deactivated(state) {
        this._deactivated = state;
    }
    /**
     * Returns the 2D position of this vertex.
     * Only valid if this MetaVertex encapsulates only one vertex.
     */
    get position() {
        if (this._vertices.length === 1) {
            return this._vertices[0].position;
        }
        throw new Error('Cannot query position of MetaVertex.');
    }
    get incidentFaces() {
        return super.incidentFaces;
    }
    get nonTabIncidentFaces() {
        return this.incidentFaces.filter(face => !face.isTab);
    }
    get nonTabIncidentEdges() {
        return this.incidentEdges.filter(edgeRef => ((edgeRef.rightFace && !edgeRef.rightFace.isTab)
            || (edgeRef.leftFace && !edgeRef.leftFace.isTab)));
    }
    /**
     * Returns an array of CreaseVertices contained in this MetaVertex.
     * @returns {CreaseVertex[]} An array of vertices contained in this MetaVertex.
     */
    get vertices() {
        return this._vertices;
    }
    get isDestroyed() {
        return this._isDestroyed;
    }
    get isSolved() {
        let isSolved = true;
        this.nonTabIncidentEdges.forEach((edge) => {
            if (edge.foldAngle === null) {
                isSolved = false;
            }
        });
        return isSolved;
    }
    get rightmostEdgeRef() {
        if (this.nonTabIncidentEdges.length === this.nonTabIncidentFaces.length) {
            // In the case of a closed vertex that needs solving,
            // just pick any edgeRef to start with
            return this.nonTabIncidentEdges[0];
        }
        return this.nonTabIncidentEdges.find(edgeRef => !edgeRef.rightFace
            || (edgeRef.rightFace && edgeRef.rightFace.isTab));
    }
    /**
     * Returns the ith incident edge reference after the specified incident edge or edge reference.
     * Edge reference must border at least one non-tab face.
     * @param {CreaseEdgeRef} edgeRef An edge reference that is incident to this vertex.
     * @param {number} [i = 1] An integer offset. The default value of 1 gets the next edge.
     * @returns {CreaseEdgeRef} The ith next incident edge reference.
     */
    getNextNonTabEdgeRef(edgeRef, i = 1) {
        const { nonTabIncidentEdges } = this;
        const index = nonTabIncidentEdges.findIndex(r => r.edge === edgeRef.edge);
        if (index < 0) {
            throw new Error('The specified edge reference is not incident to this vertex.');
        }
        const n = nonTabIncidentEdges.length;
        return nonTabIncidentEdges[((index + i) % n + n) % n];
    }
    get incidentFaceInfo() {
        const e0 = this.rightmostEdgeRef;
        let edgeRef1 = e0;
        let edgeRef2 = this.getNextNonTabEdgeRef(edgeRef1);
        const allFacesInfo = [];
        do {
            const faceInfo = this.getAngularContributionBetweenEdges(edgeRef1, edgeRef2);
            allFacesInfo.push(faceInfo);
            edgeRef1 = edgeRef2;
            edgeRef2 = this.getNextNonTabEdgeRef(edgeRef1);
        } while (edgeRef1.leftFace && !edgeRef1.leftFace.isTab && edgeRef1 !== e0);
        return allFacesInfo;
    }
    get numFaceBreaks() {
        const numFaces = this.nonTabIncidentFaces.length;
        const numEdges = this.nonTabIncidentEdges.length;
        // 0 indicates a closed loop of faces.
        return numEdges - numFaces;
    }
    getAngularContributionBetweenEdges(edgeRef1, edgeRef2) {
        const face = edgeRef1.leftFace;
        // We need to find the CreaseEdges associated with these edge refs
        // so we can query position info.
        const edge1 = edgeRef1.edge.getEdgeAdjacentToFaceAtVertex(face.face, this);
        const edge2 = edgeRef2.edge.getEdgeAdjacentToFaceAtVertex(face.face, this);
        // Create unit vectors along each edge.
        let vec1 = common_1.vec.normalize(common_1.vec.subtract(edge1.vertex2.position, edge1.vertex1.position));
        let vec2 = common_1.vec.normalize(common_1.vec.subtract(edge2.vertex2.position, edge2.vertex1.position));
        // We don't have edgeRefs associated with these edges,
        // so we need to determine their orientation.
        // This vertex1 should be inside this.vertices.
        if (this.vertices.indexOf(edge1.vertex1) < 0) {
            vec1 = common_1.vec.multiply(vec1, -1);
        }
        if (this.vertices.indexOf(edge2.vertex1) < 0) {
            vec2 = common_1.vec.multiply(vec2, -1);
        }
        // Calculate the sine and cosine of the angle between the two face edges.
        const sin = common_1.vec.wedge(vec1, vec2);
        const cos = common_1.vec.dot(vec1, vec2);
        return {
            face,
            sin,
            cos,
            edgeRef1,
            edgeRef2,
        };
    }
    get isSolvable() {
        if (this._deactivated) {
            return false;
        }
        const { nonTabIncidentEdges, nonTabIncidentFaces } = this;
        if (nonTabIncidentEdges.length === 4 && nonTabIncidentFaces.length === 3) {
            return true;
        }
        const numberOfSolvedEdges = nonTabIncidentEdges.filter(edge => edge.foldAngle !== null).length;
        // const degreesOfFreedom = this.incidentFaces - numberOfSolvedEdges - 3;
        // return degreesOfFreedom <= 1;
        return numberOfSolvedEdges !== nonTabIncidentEdges.length
            && nonTabIncidentFaces.length === 3;
    }
    get isConnectedToCrease() {
        return this.incidentEdges.find(edge => edge.isCrease) !== undefined;
    }
    solve(logDebug) {
        if (this.isSolved || this.deactivated) {
            return {
                success: false,
            };
        }
        const { nonTabIncidentFaces, numFaceBreaks } = this;
        // Ignore this vertex if there are more than one breaks between adjacent (non-tab) faces.
        // This should rarely happen.
        if (numFaceBreaks > 1) {
            return {
                success: false,
            };
        }
        // Ignore if this vertex if it has less than 3 incident faces.
        if (nonTabIncidentFaces.length < 3) {
            return {
                success: false,
            };
        }
        // TODO: we should calc degrees of freedom here,
        // if we have some solved edges we can solve more cases.
        if (nonTabIncidentFaces.length > 3) {
            // TODO: there may be a case where we have >1 tabs to assign?
            return {
                success: false,
            };
        }
        if (nonTabIncidentFaces.length > 3 && numFaceBreaks === 0) {
            return {
                success: false,
            };
        }
        // Make sure that exactly three faces meet at this vertex.
        const { incidentFaceInfo } = this;
        if (incidentFaceInfo.length !== 3) {
            return {
                success: false,
            };
        }
        // check.isArray('incidentFaceInfo', incidentFaceInfo, 3);
        // Check that adjacent edges are same length
        const { edgeRef1 } = incidentFaceInfo[0];
        const { edgeRef2 } = incidentFaceInfo[2];
        // We should only merge vertices if the edges match in length (within 10%).
        const edge1Length = edgeRef1.length;
        const edge2Length = edgeRef2.length;
        const lengthTol = 0.1;
        let incompatibleLengths = false;
        if (edge1Length < edge2Length) {
            if (edge1Length / edge2Length < 1 - lengthTol) {
                incompatibleLengths = true;
            }
        }
        else if (edge2Length / edge1Length < 1 - lengthTol) {
            incompatibleLengths = true;
        }
        if (incompatibleLengths) {
            logDebug(`Unable to merge edges: ${edgeRef1.edge.toString()},
                ${edgeRef2.edge.toString()}, incompatible lengths: ${edge1Length}, ${edge2Length}`);
            return {
                success: false,
            };
        }
        // For each incident face, calculate the sine and cosine of the angle between the two
        // face edges that meet at the current vertex.
        const faceSinCos = incidentFaceInfo.map((faceInfo) => {
            const { sin, cos } = faceInfo;
            return { sin, cos };
        });
        // If any of the sines are zero, we can't uniquely determine the fold angles.
        if (!faceSinCos.every(f => f.sin !== 0)) {
            return {
                success: false,
            };
        }
        // Calculate the cosines of the dihedral angles.
        // See https://math.stackexchange.com/questions/314970/dihedral-angles-between-tetrahedron-faces-from-triangles-angles-at-the-tip.
        const dihedralCosines = faceSinCos.map((f, i) => {
            const g = faceSinCos[(i + 1) % 3];
            const h = faceSinCos[(i + 2) % 3];
            return (f.cos - g.cos * h.cos) / (g.sin * h.sin);
        });
        // We can only determine fold angles if the cosines are between -1 and 1. If any of the
        // calculated values fall outside that range, the edges cannot possibly meet.
        if (!dihedralCosines.every(cos => cos > -1 && cos < 1)) {
            return {
                success: false,
            };
        }
        // Fold angles are π minus dihedral angles, so we use the negative of the cosine of
        // dihedral angle.
        const foldAngles = dihedralCosines.map(cos => common_1.utils.convertToDegrees(Math.acos(-cos)));
        // First check that new fold angles are consistent with any previously assigned.
        // TODO: how to pick tol for this?
        const foldAngleTol = 1e-1;
        let validFoldAngles = true;
        incidentFaceInfo.forEach((faceInfo, i) => {
            const foldAngle1 = faceInfo.edgeRef1.edge.foldAngle;
            if (foldAngle1 !== null) {
                if (Math.abs(foldAngles[(i + 1) % 3] - foldAngle1) > foldAngleTol) {
                    logDebug(`Invalid fold angles ${foldAngle1}, ${foldAngles[(i + 1) % 3]}`);
                    validFoldAngles = false;
                }
            }
            const foldAngle2 = faceInfo.edgeRef2.edge.foldAngle;
            if (foldAngle2 !== null) {
                if (Math.abs(foldAngles[(i + 2) % 3] - foldAngle2) > foldAngleTol) {
                    logDebug(`Invalid fold angles ${foldAngle2}, ${foldAngles[(i + 2) % 3]}`);
                    validFoldAngles = false;
                }
            }
        });
        if (!validFoldAngles) {
            return {
                success: false,
            };
        }
        // Update the fold angle on each edge that is a crease.
        incidentFaceInfo.forEach((faceInfo, i) => {
            faceInfo.edgeRef1.edge.foldAngle = foldAngles[(i + 1) % 3];
            faceInfo.edgeRef2.edge.foldAngle = foldAngles[(i + 2) % 3];
        });
        // Success.
        // Merge the vertices, if necessary.
        const mv1 = edgeRef1.vertex2;
        const mv2 = edgeRef2.vertex2;
        // Check that the vertices to merge are different.
        if (mv1 === mv2) {
            return {
                success: true,
            };
        }
        logDebug('    Merging vertices ', mv2.toString(), ' to ', mv1.toString());
        const me1 = edgeRef1.edge;
        const me2 = edgeRef2.edge;
        mv1.merge(mv2, mv1.getEdgeRefForEdge(me1), mv2.getEdgeRefForEdge(me2));
        // Merge the edges.
        logDebug('    Merging edges ', me2.toString(), ' to ', me1.toString());
        me1.mergeParallel(me2);
        // Check to see if there is another edge that needs merging
        // (in case we are closing a polyhedron).
        for (let i = 0; i < mv1.incidentEdges.length; i++) {
            for (let j = i + 1; j < mv1.incidentEdges.length; j++) {
                if (mv1.incidentEdges[i].vertex2 === mv1.incidentEdges[j].vertex2) {
                    const edge1 = mv1.incidentEdges[j].edge;
                    const edge2 = mv1.incidentEdges[i].edge;
                    logDebug('    Merging edges ', edge1.toString(), ' to ', edge2.toString());
                    edge2.mergeParallel(edge1);
                }
            }
        }
        return {
            success: true,
            // Add changed vertex to solve queue.
            nextVertex: mv1,
        };
    }
    /**
     * Returns the CreaseEdgeRef associated with an input MetaEdge.
     * @param {MetaEdge} edge An edge attached to this vertex.
     * @returns {CreaseEdgeRef} The edge reference associated with the MetaEdge.
     */
    getEdgeRefForEdge(edge) {
        let edgeRef;
        this.incidentEdges.forEach((ref) => {
            if (ref.edge === edge) {
                if (edgeRef) {
                    throw new Error(`More than one CreaseEdgeRef associated with edge ${edge.toString()}`);
                }
                edgeRef = ref;
            }
        });
        if (!edgeRef) {
            throw new Error(`No CreaseEdgeRef associated with edge ${edge.toString()} at vertex ${this.toString()}`);
        }
        return edgeRef;
    }
    /**
     * Remove this vertex and remove all references from adjacent edges and faces.
     */
    remove() {
        // Remove references of this vertex from neighboring edges and faces.
        if (this.incidentEdges.length) {
            throw new Error(`Remove this vertex from all incident edges before calling MetaVertex.remove(): ${this.toString()}`);
        }
        if (this.incidentFaces.length) {
            throw new Error(`Remove this vertex from all incident faces before calling MetaVertex.remove(): ${this.toString()}`);
        }
        // Remove properties of this.
        this.destroy();
    }
    /**
     * Merge another MetaVertex to this one.
     * @param {MetaVertex} vertex A MetaVertex to merge to this.
     * @param {CreaseEdgeRef} edgeRef1
     * @param {CreaseEdgeRef} edgeRef2
     * Passed in vertex is destroyed upon completion of merge.
     */
    merge(vertex, edgeRef1, edgeRef2) {
        this._vertices = this._vertices.concat(vertex._vertices);
        // Replace references of vertex with this.
        vertex.incidentEdges.forEach((edgeRef) => {
            edgeRef.edge.replaceVertex(vertex, this);
        });
        // Sort vertex.incidentEdges so that edgeRef2 is first.
        const startingIndex = vertex.incidentEdges.indexOf(edgeRef2);
        const start = vertex.incidentEdges.slice(startingIndex, vertex.incidentEdges.length);
        const end = vertex.incidentEdges.slice(0, startingIndex);
        const sortedEdges = start.concat(end);
        // Insert sorted incident edges of vertex after edgeRef1.
        const spliceIndex = this.incidentEdges.indexOf(edgeRef1) + 1;
        if (spliceIndex < 0) {
            throw new Error(`Cannot find edge: ${edgeRef1.edge.toString()} attached to vertex: ${this.toString()}`);
        }
        this.incidentEdges.splice(spliceIndex, 0, ...sortedEdges);
        vertex.incidentFaces.forEach((face) => {
            // We don't care about the order of incidentFaces.
            if (this.incidentFaces.indexOf(face) < 0) {
                this.incidentFaces.push(face);
            }
        });
        // Remove properties of vertex.
        vertex.destroy();
    }
    /**
     * Remove an edge connected to this vertex.
     * This should only be called by MetaEdge.collapse() and MetaEdge.merge().
     * @private
     * @param {MetaEdge} edge A MetaEdge to remove.
     */
    removeEdge(edge) {
        const removedEdgeRefs = [];
        // Note: in the case of merging vertices to remove thickness offsets in dieline
        // an edge may be attached to the same vertex twice before it is removed.
        // So we should not stop looking after we find the first occurance of the edge.
        for (let i = this.incidentEdges.length - 1; i >= 0; i--) {
            const incidentEdgeRef = this.incidentEdges[i];
            if (incidentEdgeRef.edge === edge) {
                // Remove EdgeRef.
                removedEdgeRefs.push(incidentEdgeRef);
                this.incidentEdges.splice(i, 1);
            }
        }
        // Remove any lingering pointers.
        removedEdgeRefs.forEach((edgeRef) => {
            edgeRef.destroy();
        });
    }
    addEdgeAtIndex(edge, index) {
        const edgeRef = new CreaseEdgeRef_1.CreaseEdgeRef(edge, edge.vertex1 !== this);
        this.incidentEdges.splice(index, 0, edgeRef);
    }
    /**
     * Remove all edges connected to this vertex.
     * This should only be called by and MetaEdge.mergeSerial().
     * @private
     */
    removeAllEdges() {
        this.incidentEdges.forEach((edgeRef) => {
            edgeRef.destroy();
        });
        this._incidentEdges = [];
    }
    /**
     * Replace an edge connected to this vertex with a new one.
     * This should only be called by MetaEdge.mergeSerial().
     * @private
     * @param {MetaEdge} oldEdge A MetaEdge to replace.
     * @param {MetaEdge} newEdge A new MetaEdge.
     */
    replaceEdge(oldEdge, newEdge) {
        this.incidentEdges.forEach((edgeRef) => {
            if (edgeRef.edge === oldEdge) {
                edgeRef._replaceEdge(newEdge);
            }
        });
    }
    /**
     * Reverse an edge connected to this vertex.
     * This should only be called by MetaEdge._reverse().
     * @private
     * @param {MetaEdge} edge A MetaEdge to reverse.
     */
    reverseEdge(edge) {
        this.incidentEdges.forEach((edgeRef) => {
            if (edgeRef.edge === edge) {
                edgeRef._reverse();
            }
        });
    }
    /**
     * Remove a face connected to this vertex.
     * This should only be called by MetaFace.remove().
     * @private
     * @param {MetaFace} face A MetaFace to remove.
     */
    removeFace(face) {
        for (let i = this.incidentFaces.length - 1; i >= 0; i--) {
            if (this.incidentFaces[i] === face) {
                this.incidentFaces.splice(i, 1);
            }
        }
    }
    toString() {
        if (!this.vertices) {
            return '[]';
        }
        return `${JSON.stringify(this.vertices.map(v => v.index))}`;
    }
    destroy() {
        delete this._vertices;
        delete this._index;
        delete this._position;
        delete this._incidentEdges;
        delete this._incidentFaces;
        this._isDestroyed = true;
    }
}
exports.MetaVertex = MetaVertex;
//# sourceMappingURL=MetaVertex.js.map